import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { affiliateService } from '../../services/affiliateService';
import { useAffiliateSessionStore } from '../../store/affiliateSessionStore';

interface ProfileData {
  contact_name: string;
  company_name: string;
  email: string;
  phone: string;
  website: string;
  payment_method: string;
  payment_email: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_routing_number: string;
  tax_id: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      className={`pt-5 ${value !== index ? 'hidden' : ''}`}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

const AffiliateProfile: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  // Track focus for masking behavior
  const [isAccountFocused, setIsAccountFocused] = useState(false);
  const [isRoutingFocused, setIsRoutingFocused] = useState(false);
  const [isTaxFocused, setIsTaxFocused] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const navigate = useNavigate();
  const { affiliateUserId, setSession, clearSession } = useAffiliateSessionStore();
  
  // Helpers: sanitize/mask PII
  const toDigits = (v: string) => v.replace(/\D+/g, '');
  const last4 = (v: string) => (v.length <= 4 ? v : v.slice(-4));
  const mask = (v: string, visible: number = 4) => (v.length === 0 ? '' : (v.length <= visible ? '*'.repeat(v.length) : '*'.repeat(Math.max(0, v.length - visible)) + v.slice(-visible)));

  // Derived masked values for display when blurred
  const displayAccount = !isAccountFocused ? mask(profileData?.bank_account_number || '') : (profileData?.bank_account_number || '');
  const displayRouting = !isRoutingFocused ? mask(profileData?.bank_routing_number || '') : (profileData?.bank_routing_number || '');
  const displayTax = !isTaxFocused ? mask(profileData?.tax_id || '') : (profileData?.tax_id || '');

  useEffect(() => {
    // Check if user is logged in
    const id = affiliateUserId;
    if (!id) {
      clearSession();
      navigate('/affiliate/login');
      return;
    }

    fetchProfileData();
  }, [navigate, affiliateUserId]);

  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);

    try {
      const id = affiliateUserId;
      
      if (!id) {
        clearSession();
        navigate('/affiliate/login');
        return;
      }

      // Verify session before fetching sensitive profile
      try {
        const verified = await affiliateService.verifySession(id);
        if (!verified?.valid) {
          clearSession();
          navigate('/affiliate/login');
          return;
        }
      } catch (e) {
        clearSession();
        navigate('/affiliate/login');
        return;
      }

      const data = await affiliateService.getProfile(id);
      setProfileData(data.profile);
      // Ensure in-memory session has user context for downstream usage
      if (!affiliateUserId) {
        const user = (data as any).affiliate || data.profile || null;
        setSession(id, user);
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.message || 'An error occurred while fetching your profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profileData) return;
    
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  // Digits-only handlers for sensitive inputs
  const handleDigitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profileData) return;
    const { name, value } = e.target;
    const digits = toDigits(value);
    setProfileData({
      ...profileData,
      [name]: digits,
    });
  };

  const handlePaymentMethodChange = (e: any) => {
    if (!profileData) return;

    setProfileData({
      ...profileData,
      payment_method: e.target.value
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChangePasswordData({
      ...changePasswordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Basic client-side validation for profile
    if (!profileData?.contact_name || profileData.contact_name.trim().length < 2) {
      setSaving(false);
      setError('Please provide a valid contact name.');
      return;
    }
    if (profileData?.phone && profileData.phone.length > 0 && !/^\+?[0-9\-\s()]{7,}$/.test(profileData.phone)) {
      setSaving(false);
      setError('Please provide a valid phone number.');
      return;
    }

    try {
      const id = affiliateUserId || '';
      await affiliateService.updateProfile(id, profileData);
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating your profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Payment validation depending on method
    const method = profileData?.payment_method;
    if (!method) {
      setSaving(false);
      setError('Please select a payment method.');
      return;
    }
    if (method === 'paypal') {
      const email = profileData?.payment_email || '';
      const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!validEmail) {
        setSaving(false);
        setError('Please provide a valid PayPal email address.');
        return;
      }
    }
    if (method === 'bank_transfer') {
      if (!profileData?.bank_account_name || profileData.bank_account_name.trim().length < 2) {
        setSaving(false);
        setError('Please provide a valid bank account name.');
        return;
      }
      if (!profileData?.bank_account_number || profileData.bank_account_number.trim().length < 6) {
        setSaving(false);
        setError('Please provide a valid bank account number.');
        return;
      }
      if (!profileData?.bank_routing_number || profileData.bank_routing_number.trim().length < 5) {
        setSaving(false);
        setError('Please provide a valid bank routing number.');
        return;
      }
    }

    try {
      const id = affiliateUserId || '';
      await affiliateService.updatePaymentDetails(id, {
        payment_method: profileData?.payment_method,
        payment_email: profileData?.payment_email,
        bank_account_name: profileData?.bank_account_name,
        bank_account_number: profileData?.bank_account_number ? toDigits(profileData.bank_account_number) : undefined,
        bank_routing_number: profileData?.bank_routing_number ? toDigits(profileData.bank_routing_number) : undefined,
        tax_id: profileData?.tax_id ? toDigits(profileData.tax_id) : undefined
      });
      setSuccess('Payment details updated successfully');
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating your payment details');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Password validation
    if (!changePasswordData.currentPassword || changePasswordData.currentPassword.length < 6) {
      setSaving(false);
      setError('Current password is required.');
      return;
    }
    if (!changePasswordData.newPassword || changePasswordData.newPassword.length < 8) {
      setSaving(false);
      setError('New password must be at least 8 characters.');
      return;
    }
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setSaving(false);
      setError('New password and confirmation do not match.');
      return;
    }

    try {
      const id = affiliateUserId || '';
      await affiliateService.changePassword(
        id,
        changePasswordData.currentPassword,
        changePasswordData.newPassword
      );
      setSuccess('Password changed successfully');
      setChangePasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      setError(err.message || 'An error occurred while changing your password');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearSession();
    navigate('/affiliate/login');
  };

  if (loading && !profileData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
        <div className="animate-spin h-8 w-8">
          <svg className="h-full w-full text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-gray-700">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white py-4 px-6 flex justify-between items-center">
        <h1 className="text-xl font-semibold">Explanatum Affiliate Portal</h1>
        <button 
          className="border border-white rounded-md px-4 py-2 text-sm font-medium text-white hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50" 
          onClick={handleLogout}
        >
          Logout
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-8">
        {/* Navigation buttons */}
        <div className="mb-8">
          <button
            className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => navigate('/affiliate/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        {/* Profile content */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                className={`px-6 py-4 text-sm font-medium w-1/3 text-center border-b-2 ${tabValue === 0 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={(e) => handleTabChange(e, 0)}
                aria-label="Personal Information tab"
              >
                Personal Information
              </button>
              <button
                className={`px-6 py-4 text-sm font-medium w-1/3 text-center border-b-2 ${tabValue === 1 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={(e) => handleTabChange(e, 1)}
                aria-label="Payment Details tab"
              >
                Payment Details
              </button>
              <button
                className={`px-6 py-4 text-sm font-medium w-1/3 text-center border-b-2 ${tabValue === 2 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={(e) => handleTabChange(e, 2)}
                aria-label="Security tab"
              >
                Security
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Personal Information Tab */}
            <TabPanel value={tabValue} index={0}>
              <form onSubmit={handleProfileSubmit}>
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 mb-2">
                      Personal Information
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        name="contact_name"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={profileData?.contact_name || ''}
                        onChange={handleProfileChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name (Optional)
                      </label>
                      <input
                        type="text"
                        id="company_name"
                        name="company_name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={profileData?.company_name || ''}
                        onChange={handleProfileChange}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={profileData?.email || ''}
                        onChange={handleProfileChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={profileData?.phone || ''}
                        onChange={handleProfileChange}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                      Website URL (Optional)
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={profileData?.website || ''}
                      onChange={handleProfileChange}
                    />
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </TabPanel>

            {/* Payment Details Tab */}
            <TabPanel value={tabValue} index={1}>
              <form onSubmit={handlePaymentDetailsSubmit}>
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 mb-2">
                      Payment Details
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method *
                      </label>
                      <select
                        id="payment_method"
                        name="payment_method"
                        value={profileData?.payment_method || ''}
                        onChange={handlePaymentMethodChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a payment method</option>
                        <option value="paypal">PayPal</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="payment_email" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Email (PayPal)
                      </label>
                      <input
                        type="email"
                        id="payment_email"
                        name="payment_email"
                        value={profileData?.payment_email || ''}
                        onChange={handleProfileChange as any}
                        disabled={profileData?.payment_method !== 'paypal'}
                        required={profileData?.payment_method === 'paypal'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>
                  </div>

                  {profileData?.payment_method === 'bank_transfer' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="bank_account_name" className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Account Name *
                        </label>
                        <input
                          type="text"
                          id="bank_account_name"
                          name="bank_account_name"
                          value={profileData?.bank_account_name || ''}
                          onChange={handleProfileChange as any}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="bank_account_number" className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Account Number *
                        </label>
                        <input
                          type="text"
                          id="bank_account_number"
                          name="bank_account_number"
                          value={displayAccount}
                          onChange={handleDigitsChange as any}
                          onFocus={() => setIsAccountFocused(true)}
                          onBlur={() => setIsAccountFocused(false)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="bank_routing_number" className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Routing Number *
                        </label>
                        <input
                          type="text"
                          id="bank_routing_number"
                          name="bank_routing_number"
                          value={displayRouting}
                          onChange={handleDigitsChange as any}
                          onFocus={() => setIsRoutingFocused(true)}
                          onBlur={() => setIsRoutingFocused(false)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="tax_id" className="block text-sm font-medium text-gray-700 mb-1">
                      Tax ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="tax_id"
                      name="tax_id"
                      value={displayTax}
                      onChange={handleDigitsChange as any}
                      onFocus={() => setIsTaxFocused(true)}
                      onBlur={() => setIsTaxFocused(false)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">For tax reporting purposes</p>
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        'Update Payment Details'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </TabPanel>

            {/* Security Tab */}
            <TabPanel value={tabValue} index={2}>
              <form onSubmit={handlePasswordSubmit}>
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 mb-2">
                      Change Password
                    </h2>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password *
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={changePasswordData.currentPassword}
                      onChange={handlePasswordChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        New Password *
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={changePasswordData.newPassword}
                        onChange={handlePasswordChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password *
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        required
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${changePasswordData.newPassword !== changePasswordData.confirmPassword && changePasswordData.confirmPassword !== '' ? 'border-red-500' : 'border-gray-300'}`}
                        value={changePasswordData.confirmPassword}
                        onChange={handlePasswordChange}
                      />
                      {changePasswordData.newPassword !== changePasswordData.confirmPassword && changePasswordData.confirmPassword !== '' && (
                        <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={saving || !changePasswordData.currentPassword || !changePasswordData.newPassword || !changePasswordData.confirmPassword || changePasswordData.newPassword !== changePasswordData.confirmPassword}
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </span>
                      ) : 'Change Password'}
                    </button>
                  </div>
                </div>
              </form>
            </TabPanel>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 mt-auto bg-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Explanatum Affiliate Portal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AffiliateProfile;
